import React, { useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';

// Selector for elements that can receive keyboard focus within the panel.
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

// ============================================
// MODAL COMPONENT
// ============================================
// A flexible modal component with backdrop and animations.
// Uses the design system tokens from tailwind.config.js

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  size?: ModalSize;
  closeOnBackdrop?: boolean;
  closeOnEscape?: boolean;
  showCloseButton?: boolean;
  children: React.ReactNode;
}

interface ModalHeaderProps {
  children: React.ReactNode;
  className?: string;
}

interface ModalBodyProps {
  children: React.ReactNode;
  className?: string;
}

interface ModalFooterProps {
  children: React.ReactNode;
  className?: string;
}

// Size styles
const sizeStyles: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  full: 'max-w-4xl',
};

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  size = 'md',
  closeOnBackdrop = true,
  closeOnEscape = true,
  showCloseButton = true,
  children,
}) => {
  // Ref to the modal panel for focus management.
  const panelRef = useRef<HTMLDivElement>(null);
  // Element that had focus before the modal opened, restored on close.
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // Handle escape key
  const handleEscape = useCallback(
    (event: KeyboardEvent) => {
      if (closeOnEscape && event.key === 'Escape') {
        onClose();
      }
    },
    [closeOnEscape, onClose]
  );

  // Trap Tab/Shift+Tab focus within the panel.
  const handleTabTrap = useCallback((event: KeyboardEvent) => {
    if (event.key !== 'Tab') return;

    const panel = panelRef.current;
    if (!panel) return;

    const focusable = Array.from(
      panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
    ).filter((el) => el.offsetParent !== null || el === document.activeElement);

    if (focusable.length === 0) {
      // Nothing focusable inside; keep focus on the panel itself.
      event.preventDefault();
      panel.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (event.shiftKey) {
      if (active === first || active === panel || !panel.contains(active)) {
        event.preventDefault();
        last.focus();
      }
    } else {
      if (active === last || !panel.contains(active)) {
        event.preventDefault();
        first.focus();
      }
    }
  }, []);

  // Handle backdrop click
  const handleBackdropClick = (event: React.MouseEvent) => {
    if (closeOnBackdrop && event.target === event.currentTarget) {
      onClose();
    }
  };

  // Add/remove keyboard listeners and lock body scroll while open.
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.addEventListener('keydown', handleTabTrap);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('keydown', handleTabTrap);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, handleEscape, handleTabTrap]);

  // On open: remember the trigger, move focus into the panel.
  // On close/unmount: restore focus to the trigger if it still exists.
  useEffect(() => {
    if (!isOpen) return;

    previousFocusRef.current = document.activeElement as HTMLElement | null;

    const panel = panelRef.current;
    if (panel) {
      const firstFocusable = panel.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      (firstFocusable ?? panel).focus();
    }

    return () => {
      const previous = previousFocusRef.current;
      if (previous && typeof previous.focus === 'function' && document.contains(previous)) {
        previous.focus();
      }
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const modalContent = (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 animate-fade-in"
        onClick={handleBackdropClick}
      />

      {/* Modal */}
      <div
        ref={panelRef}
        tabIndex={-1}
        className={`
          relative w-full ${sizeStyles[size]}
          bg-surface rounded-xl shadow-2xl border border-neutral-200
          animate-scale-in focus:outline-none
        `}
      >
        {showCloseButton && (
          <button
            onClick={onClose}
            className="
              absolute top-4 right-4 z-10
              p-1 rounded-lg
              text-neutral-400 hover:text-neutral-600
              hover:bg-neutral-100
              transition-colors duration-fast
            "
            aria-label="Close modal"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
        {children}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

// Modal Header component
const ModalHeader: React.FC<ModalHeaderProps> = ({
  children,
  className = '',
}) => {
  return (
    <div className={`px-6 py-4 border-b border-neutral-100 ${className}`.trim()}>
      <h2 className="text-lg font-semibold text-neutral-900">{children}</h2>
    </div>
  );
};

ModalHeader.displayName = 'ModalHeader';

// Modal Body component
const ModalBody: React.FC<ModalBodyProps> = ({
  children,
  className = '',
}) => {
  return (
    <div className={`px-6 py-4 ${className}`.trim()}>
      {children}
    </div>
  );
};

ModalBody.displayName = 'ModalBody';

// Modal Footer component
const ModalFooter: React.FC<ModalFooterProps> = ({
  children,
  className = '',
}) => {
  return (
    <div className={`px-6 py-4 border-t border-neutral-100 bg-neutral-50 rounded-b-xl flex justify-end gap-3 ${className}`.trim()}>
      {children}
    </div>
  );
};

ModalFooter.displayName = 'ModalFooter';

export default Modal;
export { ModalHeader, ModalBody, ModalFooter };
